import { type ReactNode, useId, useState } from "react";
import { useTranslation } from "react-i18next";

/**
 * Every chart's frame: a `<figure>` with its title and one-line description,
 * the chart, an optional legend, and a "Show as table" toggle that swaps the
 * drawing for a real table of the same numbers — the screen reader's way in
 * and the keyboard's, and the relief channel for colours that rely on their
 * word (silver, "not scheduled").
 *
 * `interactive` charts (links, focusable columns) are a labelled group; the
 * others are one `role="img"` with the title and description as its name.
 */
export function ChartFrame({
	title,
	description,
	legend,
	table,
	actions,
	interactive = false,
	className = "",
	children,
}: {
	title: ReactNode;
	description?: ReactNode;
	legend?: ReactNode;
	/** The same numbers as a table. Omitted: the chart is already one (the heatmap). */
	table?: ReactNode;
	actions?: ReactNode;
	interactive?: boolean;
	className?: string;
	children: ReactNode;
}) {
	const { t } = useTranslation("charts");
	const id = useId();
	const [asTable, setAsTable] = useState(false);
	const titleId = `${id}-title`;
	const descId = `${id}-desc`;

	return (
		<figure className={`flex min-w-0 flex-col gap-[0.75rem] ${className}`} aria-labelledby={titleId} aria-describedby={description ? descId : undefined}>
			<figcaption className="flex flex-wrap items-start justify-between gap-x-[0.75rem] gap-y-[0.375rem]">
				<span className="flex min-w-0 flex-col">
					<span id={titleId} className="font-black text-[0.9375rem] text-ink leading-tight">
						{title}
					</span>
					{description && (
						<span id={descId} className="text-[0.8125rem] text-muted">
							{description}
						</span>
					)}
				</span>
				<span className="flex flex-wrap items-center gap-[0.5rem] print:hidden">
					{actions}
					{table && (
						<button
							type="button"
							aria-pressed={asTable}
							onClick={() => setAsTable((v) => !v)}
							className="min-h-touch rounded-btn px-[0.5rem] font-bold text-[0.8125rem] text-board hover:bg-paper"
						>
							{asTable ? t("frame.showChart") : t("frame.showTable")}
						</button>
					)}
				</span>
			</figcaption>
			{asTable && table ? (
				<div className="overflow-x-auto">{table}</div>
			) : interactive ? (
				<div role="group" aria-labelledby={titleId} className="min-w-0">
					{children}
				</div>
			) : (
				<div role="img" aria-labelledby={titleId} aria-describedby={description ? descId : undefined} className="min-w-0">
					{children}
				</div>
			)}
			{legend && !asTable && <div className="print:break-inside-avoid">{legend}</div>}
		</figure>
	);
}

/** A plain data table for a chart's table view: header row, right-aligned numbers. */
export function ChartTable({ head, rows, caption }: { head: ReactNode[]; rows: ReactNode[][]; caption?: string }) {
	return (
		<table className="w-full min-w-[20rem] text-[0.8125rem]">
			{caption && <caption className="sr-only">{caption}</caption>}
			<thead className="border-rule border-b bg-paper text-[0.75rem] text-muted uppercase tracking-wide">
				<tr>
					{head.map((h, i) => (
						<th key={i} scope="col" className={`px-[0.5rem] py-[0.375rem] ${i === 0 ? "text-left" : "text-right"}`}>
							{h}
						</th>
					))}
				</tr>
			</thead>
			<tbody className="font-mono">
				{rows.map((r, i) => (
					<tr key={i} className="border-rule border-b last:border-b-0">
						{r.map((c, j) =>
							j === 0 ? (
								<th key={j} scope="row" className="px-[0.5rem] py-[0.375rem] text-left font-sans font-normal">
									{c}
								</th>
							) : (
								<td key={j} className="px-[0.5rem] py-[0.375rem] text-right">
									{c}
								</td>
							),
						)}
					</tr>
				))}
			</tbody>
		</table>
	);
}
